import * as React from 'react';
import {
  Typography,
  Box,
  Pagination,
  Grid,
  Chip,
  Paper,
  Stack,
} from '@mui/material';
import { connect } from "react-redux";
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getWhatsappTemplates, deleteWhatsappTemplate } from '../../app/store/actions/adminActions';
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformSearchBar,
  PlatformButton,
  PlatformEmptyState,
  PlatformLoadingBlock,
} from '../../app/components/platform';
import { platformColors, platformFonts } from '../../app/components/tickitPlatformTheme';

const statusChipSx = (status) => {
  const s = (status || '').toUpperCase();
  let bg = '#9e9e9e';
  if (s === 'APPROVED') bg = '#10b981';
  else if (s === 'REJECTED') bg = '#ef4444';
  else if (s === 'PENDING') bg = '#f59e0b';
  return { backgroundColor: bg, color: '#fff', fontFamily: platformFonts.body };
};

export const ViewAllTemplates = ({ getWhatsappTemplates, deleteWhatsappTemplate, templates, isLoading }) => {
  const navigate = useNavigate();

  const initialState = { search: "", page: 1, limit: 10 };
  const [formData, setFormData] = React.useState(initialState);
  const { search, page, limit } = formData;

  React.useEffect(() => {
    getWhatsappTemplates({ page, limit, search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (_event, value) => {
    setFormData({ ...formData, page: value });
    getWhatsappTemplates({ page: value, limit, search });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSearch = () => {
    setFormData({ ...formData, page: 1 });
    getWhatsappTemplates({ page: 1, limit, search });
  };

  const handleClearFilter = () => {
    setFormData(initialState);
    getWhatsappTemplates({ page: 1, limit: initialState.limit, search: initialState.search });
  };

  const handleDeleteTemplate = (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    deleteWhatsappTemplate(tpl._id, navigate, () =>
      getWhatsappTemplates({ page, limit, search })
    );
  };

  const totalDocs = templates?.totalDocs ?? templates?.docs?.length ?? 0;

  return (
    <PlatformPage>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <PlatformPageHeader
          title="WhatsApp templates"
          subtitle="Manage approved message templates"
          actions={
            <PlatformButton
              variant="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate('/Admin/createTemplate')}
            >
              Create template
            </PlatformButton>
          }
        />

        <PlatformCard title="Search" sx={{ mb: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <PlatformSearchBar
                value={search}
                onChange={(e) => handleChange({ target: { name: 'search', value: e.target.value } })}
                placeholder="Search templates…"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <PlatformButton variant="primary" onClick={handleSearch} sx={{ width: '100%' }}>
                Search
              </PlatformButton>
            </Grid>
            <Grid item xs={6} md={2}>
              <PlatformButton variant="ghost" onClick={handleClearFilter} sx={{ width: '100%' }}>
                Clear
              </PlatformButton>
            </Grid>
          </Grid>
        </PlatformCard>

        <PlatformCard
          title="Results"
          headerAction={
            <Typography
              component="span"
              aria-live="polite"
              sx={{ fontFamily: platformFonts.body, fontSize: '0.8125rem', color: 'var(--platform-text-3)' }}
            >
              {isLoading ? 'Loading…' : `${totalDocs} template${totalDocs === 1 ? '' : 's'}`}
            </Typography>
          }
        >
          {isLoading ? (
            <PlatformLoadingBlock />
          ) : templates?.docs?.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {templates.docs.map((tpl) => {
                  const status = (tpl.status || '').toUpperCase();
                  const statusLabel = status ? status.charAt(0) + status.slice(1).toLowerCase() : 'Unknown';
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={tpl._id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          height: '100%',
                          borderRadius: '12px',
                          border: `1px solid ${platformColors.border}`,
                          bgcolor: platformColors.surface,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography sx={{ fontFamily: platformFonts.display, fontWeight: 700, fontSize: '0.9375rem', color: 'var(--platform-text)', mb: 0.5 }}>
                          {tpl.name}
                        </Typography>
                        <Typography sx={{ fontFamily: platformFonts.body, fontSize: '13px', color: 'var(--platform-text-3)', mb: 1.5 }}>
                          Language: {tpl.language}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                          <Chip label={`Status: ${statusLabel}`} size="small" sx={statusChipSx(tpl.status)} />
                          <Chip
                            label={new Date(tpl.createdAt).toLocaleDateString()}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: platformColors.brand, color: platformColors.brand, fontFamily: platformFonts.body }}
                          />
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                          <PlatformButton
                            variant="ghost"
                            size="small"
                            startIcon={<EditOutlinedIcon />}
                            onClick={() => navigate(`/Admin/editTemplate/${tpl._id}`)}
                          >
                            Edit
                          </PlatformButton>
                          <PlatformButton
                            variant="danger"
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => handleDeleteTemplate(tpl)}
                          >
                            Delete
                          </PlatformButton>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={templates?.pages || 1}
                  page={page}
                  onChange={handlePageChange}
                  sx={{ '& .Mui-selected': { bgcolor: `${platformColors.brandPale} !important`, color: platformColors.brand } }}
                />
              </Box>
            </>
          ) : (
            <PlatformEmptyState
              title="No templates found"
              description="Try adjusting your search or create a new template."
              actionLabel="Create template"
              onAction={() => navigate('/Admin/createTemplate')}
            />
          )}
        </PlatformCard>
      </motion.div>
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  templates: state?.records?.results?.templates,
  isLoading: state?.records?.isLoading,
});

export default connect(mapStateToProps, { getWhatsappTemplates, deleteWhatsappTemplate })(ViewAllTemplates);
